import type { UserDetailsDto } from '@/rest/apiv2.hsdesk';
import type { User } from '@/types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function mapRole(value: unknown) {
  if (typeof value === 'string') {
    return {
      roleId: value,
      roleName: value,
      enabled: true
    };
  }

  if (!isRecord(value)) {
    return null;
  }

  const roleId = String(value.roleId ?? value.id ?? value.name ?? '').trim();
  const roleName = String(
    value.roleName ?? value.name ?? value.displayName ?? roleId
  ).trim();

  if (!roleId && !roleName) {
    return null;
  }

  return {
    roleId: roleId || roleName,
    roleName: roleName || roleId,
    description:
      typeof value.description === 'string' ? value.description : undefined,
    enabled: typeof value.enabled === 'boolean' ? value.enabled : true
  };
}

function readProfileRoles(profile: UserDetailsDto) {
  const value = profile as UserDetailsDto & {
    roles?: unknown[];
    userRoles?: unknown[];
    assignedRoles?: unknown[];
    roleNames?: string[];
  };
  const rawRoles =
    value.roles ??
    value.userRoles ??
    value.assignedRoles ??
    value.roleNames ??
    [];

  return Array.isArray(rawRoles)
    ? rawRoles
        .map(mapRole)
        .filter((role): role is NonNullable<typeof role> => Boolean(role))
    : [];
}

export function mapBackendUserToUser(profile: UserDetailsDto): User {
  const email = profile.email ?? '';
  const firstName =
    profile.firstName || profile.userName || email || 'Uzytkownik';
  const lastName = profile.lastName || '';

  return {
    id: profile.id ?? '',
    firstName,
    lastName,
    imgUrl: profile.imageUrl ?? '',
    userName: profile.userName || email,
    phone: {
      number: profile.phoneNumber ?? '',
      verified: false
    },
    sex: 'male',
    birthday: '',
    lang: 'en',
    country: '',
    city: '',
    address1: '',
    zipcode: 0,
    email: {
      name: email,
      verified: Boolean(profile.emailConfirmed)
    },
    roles: readProfileRoles(profile),
    name: `${firstName} ${lastName}`.trim() || email
  };
}
